import React, { useEffect, useMemo, useState } from "react";import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BankDataUploadDialog from "@/components/BankDataUploadDialog";
import BatchRowsDialog from "@/components/BatchRowsDialog";
import { Queue as QueueIcon, CheckCircle, Trash, FileArrowUp, ArrowClockwise, ArrowUUpLeft, WarningCircle, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import FireworksBurst from "@/components/FireworksBurst";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => {
    if (!d) return "—";
    const s = String(d);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return s;
};
const fmtDT = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function VerificationQueue() {
    const [tab, setTab] = useState("pending");
    const [data, setData] = useState({ items: [], pending_count: 0, matched_count: 0 });
    const [batches, setBatches] = useState({ batches: [], legacy_pending_count: 0 });
    const [busy, setBusy] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [queueBurst, setQueueBurst] = useState(false); // v1.25.48 — queue-empty celebration
    const prevPendingRef = React.useRef(null);
    // v1.24.14 — batch bucket drill-in
    const [drill, setDrill] = useState({ open: false, batchId: null, bucket: null, meta: null });
    const openDrill = (batch, bucket) => setDrill({ open: true, batchId: batch.id, bucket, meta: batch });

    const load = async () => {
        setBusy(true);
        try {
            const [q, b] = await Promise.all([
                api.get("/admin/verification-queue", { params: { status: tab } }),
                api.get("/admin/bank-upload-batches"),
            ]);
            setData(q.data);
            setBatches(b.data);
            setSelected(new Set());
        } catch {
            toast.error("Failed to load queue");
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    // v1.25.48 — Celebrate the moment the pending queue drops from >0 to 0.
    useEffect(() => {
        const now = Number(data?.pending_count ?? 0);
        const prev = prevPendingRef.current;
        if (prev != null && prev > 0 && now === 0) setQueueBurst(true);
        prevPendingRef.current = now;
    }, [data?.pending_count]);

    const toggleOne = (id) => setSelected((prev) => {
        const s = new Set(prev);
        s.has(id) ? s.delete(id) : s.add(id);
        return s;
    });
    const allIds = useMemo(() => data.items.map((r) => r.id), [data.items]);
    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));

    const removeOne = async (id, utr) => {
        if (!window.confirm(`Remove queued row for UTR ${utr}?`)) return;
        try {
            await api.delete(`/admin/verification-queue/${id}`);
            toast.success("Removed from queue");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const bulkDelete = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        if (!window.confirm(`Delete ${ids.length} selected queue row${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
        try {
            const { data: r } = await api.post("/admin/verification-queue/bulk-delete", { ids });
            toast.success(`Deleted ${r.deleted} rows`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
    };

    const deleteAllLegacy = async () => {
        if (!window.confirm(
            `Wipe ALL ${batches.legacy_pending_count} pending rows that were uploaded before batch tracking (v1.24.13) started?\n\n` +
            `This is the safe way to clean up an old wrong upload. Slips already auto-approved from that upload will NOT be reverted — use a fresh batch's Reverse button for that.`
        )) return;
        try {
            const { data: r } = await api.post("/admin/verification-queue/bulk-delete", { all_legacy_pending: true });
            toast.success(`Deleted ${r.deleted} legacy pending rows`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const reverseBatch = async (batchId, filename, approvedCount) => {
        if (!window.confirm(
            `Reverse this bank upload?\n\n` +
            `• File: ${filename || "(unnamed)"}\n` +
            `• All queue rows from this upload will be deleted\n` +
            `• ${approvedCount} auto-approved slip${approvedCount === 1 ? "" : "s"} will revert to PENDING and their ledger entries removed\n\n` +
            `This is the correct fix for a mistaken upload (e.g. UTR / Amount columns swapped).`
        )) return;
        try {
            const { data: r } = await api.post(`/admin/bank-upload-batches/${batchId}/reverse`);
            toast.success(`Reversed: removed ${r.queue_deleted} queue rows, reverted ${r.slips_reverted} slips`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Reverse failed"); }
    };

    return (
        <div className="space-y-6" data-testid="verification-queue-page">
            <FireworksBurst open={queueBurst} onClose={() => setQueueBurst(false)} />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Reports</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Verification Queue</h1>
                    <p className="mt-2 text-sm text-slate-500">
                        Bank rows waiting to be matched with party slips. The moment a party posts a slip with a queued UTR, it auto-approves.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button data-testid="queue-refresh" variant="outline" onClick={load} disabled={busy} className="border-slate-300">
                        <ArrowClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                    <Button data-testid="queue-open-upload" onClick={() => setUploadOpen(true)} className="bg-indigo-700 text-white hover:bg-indigo-800">
                        <FileArrowUp size={14} className="mr-1.5" /> Upload Bank Data
                    </Button>
                </div>
            </div>

            {/* Recent upload batches — where each row of your Excel actually went */}
            {batches.batches.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="batches-panel">
                    <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                        <Info size={13} weight="fill" /> Recent Bank Uploads — where each row went
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">When · File</th>
                                    <th className="px-3 py-2 text-right">Total rows</th>
                                    <th className="px-3 py-2 text-right text-emerald-800">Auto-approved</th>
                                    <th className="px-3 py-2 text-right text-amber-800">Queued</th>
                                    <th className="px-3 py-2 text-right text-slate-600">Already queued</th>
                                    <th className="px-3 py-2 text-right text-slate-600">Already verified</th>
                                    <th className="px-3 py-2 text-right text-slate-600">Bad rows</th>
                                    <th className="px-3 py-2 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {batches.batches.map((b) => (
                                    <tr key={b.id} data-testid={`batch-${b.id}`} className={b.reversed_at ? "bg-rose-50/40 text-slate-400" : ""}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-slate-800">{fmtDT(b.created_at)}</div>
                                            <div className="text-slate-500">{b.filename || "(no filename)"}{b.reversed_at && <span className="ml-1 rounded bg-rose-100 px-1 text-[9px] font-bold uppercase text-rose-700">Reversed</span>}</div>
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold tabular-nums">
                                            <button data-testid={`cell-total-${b.id}`} onClick={() => openDrill(b, "all")} className="rounded px-1 hover:bg-slate-100 hover:text-slate-900 hover:underline">{b.total_rows}</button>
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-emerald-800">
                                            <button data-testid={`cell-approved-${b.id}`} onClick={() => openDrill(b, "approved")} className="rounded px-1 hover:bg-emerald-100 hover:underline">{b.approved_count}</button>
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-amber-800">
                                            <button data-testid={`cell-queued-${b.id}`} onClick={() => openDrill(b, "queued")} className="rounded px-1 hover:bg-amber-100 hover:underline">{b.queued_count}</button>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            <button data-testid={`cell-already-queued-${b.id}`} onClick={() => openDrill(b, "already_queued")} className="rounded px-1 hover:bg-slate-100 hover:underline">{b.already_queued_count}</button>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-blue-800">
                                            <button data-testid={`cell-already-verified-${b.id}`} onClick={() => openDrill(b, "already_verified")} className="rounded px-1 hover:bg-blue-100 hover:underline">{b.already_verified_count ?? 0}</button>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                                            <button data-testid={`cell-unparseable-${b.id}`} onClick={() => openDrill(b, "unparseable")} className="rounded px-1 hover:bg-rose-100 hover:underline">{b.unparseable_count ?? 0}</button>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {!b.reversed_at ? (
                                                <Button
                                                    data-testid={`reverse-batch-${b.id}`}
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => reverseBatch(b.id, b.filename, b.approved_count)}
                                                    className="h-7 border-rose-300 text-xs text-rose-700 hover:bg-rose-50"
                                                >
                                                    <ArrowUUpLeft size={11} className="mr-1" /> Reverse
                                                </Button>
                                            ) : (
                                                <span className="text-[10px] text-slate-400">{fmtDT(b.reversed_at)}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
                        <strong>How to read this:</strong> Total = <span className="text-emerald-700">Auto-approved</span> + <span className="text-amber-700">Queued</span> + Already queued + Already verified + Bad rows. Rows already in the queue with the same UTR aren't added again — that's usually why the count on the queue page is smaller than the file.
                    </div>
                </div>
            )}

            {/* Legacy rows (no batch_id) — from uploads before v1.24.13 */}
            {batches.legacy_pending_count > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="legacy-banner">
                    <div className="flex items-start gap-2">
                        <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                        <div>
                            <div className="font-semibold">{batches.legacy_pending_count} pending rows from an OLD upload (before batch tracking)</div>
                            <div className="text-[11px] text-amber-800/80">These can't be reversed as a batch, but you can wipe them all here — or select & delete individually below.</div>
                        </div>
                    </div>
                    <Button
                        data-testid="delete-legacy-btn"
                        variant="outline"
                        onClick={deleteAllLegacy}
                        className="h-8 border-amber-400 text-xs text-amber-900 hover:bg-amber-100"
                    >
                        <Trash size={12} className="mr-1" /> Wipe {batches.legacy_pending_count} legacy rows
                    </Button>
                </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-800">
                        <QueueIcon size={13} /> Awaiting party slips
                    </div>
                    <div data-testid="pending-count" className="mt-1 font-display text-2xl font-bold text-amber-900 tabular-nums">{data.pending_count}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-800">
                        <CheckCircle size={13} /> Matched (already auto-approved)
                    </div>
                    <div data-testid="matched-count" className="mt-1 font-display text-2xl font-bold text-emerald-900 tabular-nums">{data.matched_count}</div>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="pending" data-testid="tab-pending">Pending ({data.pending_count})</TabsTrigger>
                    <TabsTrigger value="matched" data-testid="tab-matched">Matched ({data.matched_count})</TabsTrigger>
                </TabsList>

                {/* Selection toolbar for pending tab */}
                {tab === "pending" && selected.size > 0 && (
                    <div className="mt-3 flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm" data-testid="bulk-toolbar">
                        <div className="text-rose-900">
                            <span className="font-bold tabular-nums">{selected.size}</span> row{selected.size === 1 ? "" : "s"} selected
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setSelected(new Set())} className="h-8 border-slate-300 text-xs">Clear</Button>
                            <Button data-testid="bulk-delete-btn" size="sm" onClick={bulkDelete} className="h-8 bg-rose-700 text-xs text-white hover:bg-rose-800">
                                <Trash size={12} className="mr-1" /> Delete Selected
                            </Button>
                        </div>
                    </div>
                )}

                <TabsContent value={tab} className="mt-4">
                    {data.items.length === 0 ? (
                        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center">
                            <QueueIcon size={32} weight="duotone" className="mx-auto text-slate-300" />
                            <div className="mt-2 text-sm text-slate-500">
                                {tab === "pending" ? "Nothing waiting — every bank row is matched." : "No matched rows yet."}
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    <tr>
                                        {tab === "pending" && (
                                            <th className="w-10 px-3 py-2">
                                                <Checkbox
                                                    data-testid="select-all-checkbox"
                                                    checked={allSelected}
                                                    onCheckedChange={toggleAll}
                                                    aria-label="Select all"
                                                />
                                            </th>
                                        )}
                                        <th className="px-4 py-2 text-left">Bank Date · Time</th>
                                        <th className="px-4 py-2 text-left">UTR</th>
                                        <th className="px-4 py-2 text-right">Amount</th>
                                        <th className="px-4 py-2 text-left">Uploaded</th>
                                        {tab === "matched" && <th className="px-4 py-2 text-left">Matched at</th>}
                                        {tab === "pending" && <th className="px-4 py-2 text-right">Action</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {data.items.map((r) => (
                                        <tr key={r.id} data-testid={`queue-row-${r.id}`} className={selected.has(r.id) ? "bg-rose-50/60" : "hover:bg-slate-50/60"}>
                                            {tab === "pending" && (
                                                <td className="w-10 px-3 py-2">
                                                    <Checkbox
                                                        data-testid={`row-checkbox-${r.id}`}
                                                        checked={selected.has(r.id)}
                                                        onCheckedChange={() => toggleOne(r.id)}
                                                    />
                                                </td>
                                            )}
                                            <td className="px-4 py-2 text-xs">
                                                <div className="font-medium text-slate-800">{fmtDate(r.date)}</div>
                                                <div className="text-slate-500">{r.time || "—"}</div>
                                            </td>
                                            <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.utr}</td>
                                            <td className="px-4 py-2 text-right font-semibold tabular-nums text-slate-800">{fmt(r.amount)}</td>
                                            <td className="px-4 py-2 text-xs text-slate-500">{fmtDT(r.uploaded_at)}</td>
                                            {tab === "matched" && <td className="px-4 py-2 text-xs text-slate-500">{fmtDT(r.matched_at)}</td>}
                                            {tab === "pending" && (
                                                <td className="px-4 py-2 text-right">
                                                    <Button
                                                        data-testid={`queue-delete-${r.id}`}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeOne(r.id, r.utr)}
                                                        className="h-7 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                                                    >
                                                        <Trash size={12} className="mr-1" /> Remove
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <BankDataUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={load} />
            <BatchRowsDialog
                open={drill.open}
                onOpenChange={(v) => setDrill((d) => ({ ...d, open: v }))}
                batchId={drill.batchId}
                bucket={drill.bucket}
                batchMeta={drill.meta}
            />
        </div>
    );
}
